import React, {
	Dispatch,
	RefAttributes,
	memo,
	useCallback,
	useMemo,
	useState,
} from "react";
import { Stepper, react, TextBox, ToggleButton, useRefWithSetter } from "uikit";
import { FieldErrors } from "react-hook-form";
import { useTranslation } from "react-i18next";

import useObjectEditor from "../../../../../../hooks/useObjectEditor";
import PaymentSystem, {
	PaymentSystemFrontEndType,
} from "../../../../../../services/PaymentSystem";
import {
	StyledColumn,
	StyledRow,
	StyledP,
} from "../../../../../../components/common";
import translationPath from "../../constants/translationPath";
import ValueStepper from "../ValueStepper";

import InternalController from "./Controller";
import CybersourcePaymentsCurrency from "./components/CybersourcePaymentsCurrency";
import CybersourcePaymentsCountryIso from "./components/CybersourcePaymentsCountryIso";

const CybersourcePaymentBase = react.withController<
	CybersourcePayment.PropsBase,
	CybersourcePayment.Controller
>(({ value, onChange, controller }) => {
	const { t } = useTranslation();

	const [countryIsoRef, setCountryIsoRef] =
		useRefWithSetter<CybersourcePaymentsCountryIso.Ref | null>(null);
	const [currencyRef, setCurrencyRef] =
		useRefWithSetter<CybersourcePaymentsCurrency.Ref | null>(null);

	const valueEditor = useObjectEditor(value, onChange);

	const type = valueEditor.useGetter("type");

	const isExecutor = useMemo(
		() =>
			PaymentSystemFrontEndType.EXECUTOR === type ||
			PaymentSystemFrontEndType.TEMPLATE_EXECUTOR === type,
		[type],
	);

	const isCustomer = useMemo(
		() =>
			PaymentSystemFrontEndType.CUSTOMER === type ||
			PaymentSystemFrontEndType.TEMPLATE_CUSTOMER === type,
		[type],
	);

	// systemFeeAdditionalForExecutor
	const systemFeeAdditionalForExecutorValue = valueEditor.useGetter(
		"systemFeeAdditionalForExecutor",
	);
	const setSystemFeeAdditionalForExecutorValue = valueEditor.useSetter(
		"systemFeeAdditionalForExecutor",
	);
	const handleSystemFeeAdditionalForExecutor = useCallback(
		(value: Stepper.Value) => {
			if (typeof value === "number") {
				setSystemFeeAdditionalForExecutorValue(value);
			}
		},
		[setSystemFeeAdditionalForExecutorValue],
	);

	const textSystemFeeAdditionalForExecutor = useMemo(
		() =>
			t([
				`${translationPath}.modal.text.systemFeeAdditionalForExecutor`,
				"Additional commission from the driver in UAH.",
			]),
		[t],
	);

	// systemFeePercentageForExecutor
	const systemFeePercentageForExecutorValue = valueEditor.useGetter(
		"systemFeePercentageForExecutor",
	);
	const setSystemFeePercentageForExecutorValue = valueEditor.useSetter(
		"systemFeePercentageForExecutor",
	);
	const handleSystemFeePercentageForExecutor = useCallback(
		(value: Stepper.Value) => {
			if (typeof value === "number") {
				setSystemFeePercentageForExecutorValue(value);
			}
		},
		[setSystemFeePercentageForExecutorValue],
	);

	const textSystemFeePercentageForExecutor = useMemo(
		() =>
			t([
				`${translationPath}.modal.text.systemFeePercentageForExecutor`,
				"Additional driver commission in %",
			]),
		[t],
	);

	// cybersourceMerchantId
	const cybersourceMerchantId = valueEditor.useGetter(
		"cybersourceMerchantId",
	);
	const setCybersourceMerchantId = valueEditor.useSetter(
		"cybersourceMerchantId",
	);
	const [errorCybersourceMerchantId, setCybersourceMerchantIdError] =
		useState<boolean>(false);
	const handleCybersourceMerchantId = useCallback(
		(value: string) => {
			if (typeof value === "string") {
				setCybersourceMerchantId(value);
				setCybersourceMerchantIdError(false);
			}
		},
		[setCybersourceMerchantId, setCybersourceMerchantIdError],
	);

	const textErrorCybersourceMerchantId = useMemo(() => {
		if (!errorCybersourceMerchantId) return "";
		return t([
			`${translationPath}.modal.errors.cybersourceMerchantId`,
			"Merchant ID not entered",
		]);
	}, [t, errorCybersourceMerchantId]);

	const placeholderCybersourceMerchantId = useMemo(
		() =>
			t([
				`${translationPath}.modal.placeholders.cybersourceMerchantId`,
				"Enter Merchant ID",
			]),
		[t],
	);

	const textCybersourceMerchantId = useMemo(
		() =>
			t([
				`${translationPath}.modal.text.cybersourceMerchantId`,
				"Merchant ID",
			]),
		[t],
	);

	// cybersourceApiKeyId
	const cybersourceApiKeyId = valueEditor.useGetter("cybersourceApiKeyId");
	const setCybersourceApiKeyId = valueEditor.useSetter("cybersourceApiKeyId");
	const [errorCybersourceApiKeyId, setCybersourceApiKeyIdError] =
		useState<boolean>(false);
	const handleCybersourceApiKeyId = useCallback(
		(value: string) => {
			if (typeof value === "string") {
				setCybersourceApiKeyId(value);
				setCybersourceApiKeyIdError(false);
			}
		},
		[setCybersourceApiKeyId, setCybersourceApiKeyIdError],
	);

	const textErrorCybersourceApiKeyId = useMemo(() => {
		if (!errorCybersourceApiKeyId) return "";
		return t([
			`${translationPath}.modal.errors.cybersourceApiKeyId`,
			"API Key ID not entered",
		]);
	}, [t, errorCybersourceApiKeyId]);

	const placeholderCybersourceApiKeyId = useMemo(
		() =>
			t([
				`${translationPath}.modal.placeholders.cybersourceApiKeyId`,
				"Enter API Key ID",
			]),
		[t],
	);

	const textCybersourceApiKeyId = useMemo(
		() =>
			t([
				`${translationPath}.modal.text.cybersourceApiKeyId`,
				"API Key ID",
			]),
		[t],
	);

	// cybersourceApiSecret
	const cybersourceApiSecret = valueEditor.useGetter("cybersourceApiSecret");
	const setCybersourceApiSecret = valueEditor.useSetter(
		"cybersourceApiSecret",
	);
	const [errorCybersourceApiSecret, setCybersourceApiSecretError] =
		useState<boolean>(false);
	const handleCybersourceApiSecret = useCallback(
		(value: string) => {
			if (typeof value === "string") {
				setCybersourceApiSecret(value);
				setCybersourceApiSecretError(false);
			}
		},
		[setCybersourceApiSecret, setCybersourceApiSecretError],
	);

	const textErrorCybersourceApiSecret = useMemo(() => {
		if (!errorCybersourceApiSecret) return "";
		return t([
			`${translationPath}.modal.errors.cybersourceApiSecret`,
			"Shared secret key not entered",
		]);
	}, [t, errorCybersourceApiSecret]);

	const placeholderCybersourceApiSecret = useMemo(
		() =>
			t([
				`${translationPath}.modal.placeholders.cybersourceApiSecret`,
				"Enter shared secret key",
			]),
		[t],
	);

	const textCybersourceApiSecret = useMemo(
		() =>
			t([
				`${translationPath}.modal.text.cybersourceApiSecret`,
				"Shared Secret Key",
			]),
		[t],
	);

	// cybersourceApiUrl
	const cybersourceApiUrl = valueEditor.useGetter("cybersourceApiUrl");
	const setCybersourceApiUrl = valueEditor.useSetter("cybersourceApiUrl");
	const [errorCybersourceApiUrl, setCybersourceApiUrlError] =
		useState<boolean>(false);
	const handleCybersourceApiUrl = useCallback(
		(value: string) => {
			if (typeof value === "string") {
				setCybersourceApiUrl(value);
				setCybersourceApiUrlError(false);
			}
		},
		[setCybersourceApiUrl],
	);

	const textErrorCybersourceApiUrl = useMemo(() => {
		if (!errorCybersourceApiUrl) return "";
		return t([
			`${translationPath}.modal.errors.cybersourceApiUrl`,
			"API host not entered",
		]);
	}, [t, errorCybersourceApiUrl]);

	const placeholderCybersourceApiUrl = useMemo(
		() =>
			t([
				`${translationPath}.modal.placeholders.cybersourceApiUrl`,
				"e.g. apitest.cybersource.com",
			]),
		[t],
	);

	const textCybersourceApiUrl = useMemo(
		() =>
			t([`${translationPath}.modal.text.cybersourceApiUrl`, "API Host"]),
		[t],
	);

	// cybersourceOrganizationId
	const cybersourceOrganizationId =
		valueEditor.useGetter("cybersourceOrganizationId") ?? "";
	const setCybersourceOrganizationId = valueEditor.useSetter(
		"cybersourceOrganizationId",
	);
	const handleCybersourceOrganizationId = useCallback(
		(value: string) => {
			if (typeof value === "string") {
				setCybersourceOrganizationId(value);
			}
		},
		[setCybersourceOrganizationId],
	);

	const placeholderCybersourceOrganizationId = useMemo(
		() =>
			t([
				`${translationPath}.modal.placeholders.cybersourceOrganizationId`,
				"Enter Organization ID (optional)",
			]),
		[t],
	);

	const textCybersourceOrganizationId = useMemo(
		() =>
			t([
				`${translationPath}.modal.text.cybersourceOrganizationId`,
				"Organization ID",
			]),
		[t],
	);

	// cybersourceCreateTripReports (default ON)
	const cybersourceCreateTripReportsRaw = valueEditor.useGetter(
		"cybersourceCreateTripReports",
	);
	const cybersourceCreateTripReports =
		cybersourceCreateTripReportsRaw ?? true;
	const setCybersourceCreateTripReports = valueEditor.useSetter(
		"cybersourceCreateTripReports",
	);
	const textCreateTripReports = useMemo(
		() =>
			t([
				`${translationPath}.modal.text.cybersourceCreateTripReports`,
				"Create trip reports",
			]),
		[t],
	);

	// cybersourceMerchantLegalName
	const cybersourceMerchantLegalName =
		valueEditor.useGetter("cybersourceMerchantLegalName") ?? "";
	const setCybersourceMerchantLegalName = valueEditor.useSetter(
		"cybersourceMerchantLegalName",
	);
	const textCybersourceMerchantLegalName = useMemo(
		() =>
			t([
				`${translationPath}.modal.text.cybersourceMerchantLegalName`,
				"Merchant legal name",
			]),
		[t],
	);
	const placeholderCybersourceMerchantLegalName = useMemo(
		() =>
			t([
				`${translationPath}.modal.placeholders.cybersourceMerchantLegalName`,
				'e.g. "TAXI SERVICE"',
			]),
		[t],
	);

	// cybersourceMerchantWebsite
	const cybersourceMerchantWebsite =
		valueEditor.useGetter("cybersourceMerchantWebsite") ?? "";
	const setCybersourceMerchantWebsite = valueEditor.useSetter(
		"cybersourceMerchantWebsite",
	);
	const textCybersourceMerchantWebsite = useMemo(
		() =>
			t([
				`${translationPath}.modal.text.cybersourceMerchantWebsite`,
				"Merchant website",
			]),
		[t],
	);
	const placeholderCybersourceMerchantWebsite = useMemo(
		() =>
			t([
				`${translationPath}.modal.placeholders.cybersourceMerchantWebsite`,
				"e.g. https://example.com",
			]),
		[t],
	);

	// cybersourceSupportContacts
	const cybersourceSupportContacts =
		valueEditor.useGetter("cybersourceSupportContacts") ?? "";
	const setCybersourceSupportContacts = valueEditor.useSetter(
		"cybersourceSupportContacts",
	);
	const textCybersourceSupportContacts = useMemo(
		() =>
			t([
				`${translationPath}.modal.text.cybersourceSupportContacts`,
				"Customer support contacts",
			]),
		[t],
	);
	const placeholderCybersourceSupportContacts = useMemo(
		() =>
			t([
				`${translationPath}.modal.placeholders.cybersourceSupportContacts`,
				"Phone / email of the support service",
			]),
		[t],
	);

	// Currency and CountryIso
	const textCurrency = useMemo(
		() => t([`${translationPath}.modal.text.currency`, "Currency"]),
		[t],
	);
	const textCountryIso = useMemo(
		() => t([`${translationPath}.modal.text.countryIso`, "Country"]),
		[t],
	);

	// controller
	controller.setContext({
		cybersourceMerchantId,
		cybersourceApiKeyId,
		cybersourceApiSecret,
		cybersourceApiUrl,
		currencyRef,
		countryIsoRef,
		setCybersourceMerchantIdError,
		setCybersourceApiKeyIdError,
		setCybersourceApiSecretError,
		setCybersourceApiUrlError,
	});

	return (
		<StyledColumn gap="10px" w="100%">
			<StyledRow alignItems="center" gap="10px" w="100%">
				<StyledP w="200px">{textCybersourceMerchantId} :</StyledP>
				<TextBox.TextBox
					style={{ width: "calc(100% - 200px)" }}
					placeholder={placeholderCybersourceMerchantId}
					error={textErrorCybersourceMerchantId}
					value={cybersourceMerchantId}
					onChange={handleCybersourceMerchantId}
				/>
			</StyledRow>

			<StyledRow alignItems="center" gap="10px" w="100%">
				<StyledP w="200px">{textCybersourceApiKeyId} :</StyledP>
				<TextBox.TextBox
					style={{ width: "calc(100% - 200px)" }}
					placeholder={placeholderCybersourceApiKeyId}
					error={textErrorCybersourceApiKeyId}
					value={cybersourceApiKeyId}
					onChange={handleCybersourceApiKeyId}
				/>
			</StyledRow>

			<StyledRow alignItems="center" gap="10px" w="100%">
				<StyledP w="200px">{textCybersourceApiSecret} :</StyledP>
				<TextBox.TextBox
					style={{ width: "calc(100% - 200px)" }}
					placeholder={placeholderCybersourceApiSecret}
					error={textErrorCybersourceApiSecret}
					value={cybersourceApiSecret}
					onChange={handleCybersourceApiSecret}
				/>
			</StyledRow>

			<StyledRow alignItems="center" gap="10px" w="100%">
				<StyledP w="200px">{textCybersourceApiUrl} :</StyledP>
				<TextBox.TextBox
					style={{ width: "calc(100% - 200px)" }}
					placeholder={placeholderCybersourceApiUrl}
					error={textErrorCybersourceApiUrl}
					value={cybersourceApiUrl}
					onChange={handleCybersourceApiUrl}
				/>
			</StyledRow>

			<StyledRow alignItems="center" gap="10px" w="100%">
				<StyledP w="200px">{textCybersourceOrganizationId} :</StyledP>
				<TextBox.TextBox
					style={{ width: "calc(100% - 200px)" }}
					placeholder={placeholderCybersourceOrganizationId}
					value={cybersourceOrganizationId}
					onChange={handleCybersourceOrganizationId}
				/>
			</StyledRow>

			{isExecutor && (
				<>
					<StyledRow alignItems="center" gap="10px" w="100%">
						<StyledP w="200px">
							{textSystemFeePercentageForExecutor}:
						</StyledP>
						<ValueStepper
							width="80px"
							min={0}
							value={systemFeePercentageForExecutorValue}
							onChange={handleSystemFeePercentageForExecutor}
						/>
					</StyledRow>

					<StyledRow alignItems="center" gap="10px" w="100%">
						<StyledP w="200px">
							{textSystemFeeAdditionalForExecutor}:
						</StyledP>
						<ValueStepper
							width="80px"
							min={0}
							value={systemFeeAdditionalForExecutorValue}
							onChange={handleSystemFeeAdditionalForExecutor}
						/>
					</StyledRow>
				</>
			)}

			{isCustomer && (
				<>
					<StyledRow
						alignItems="center"
						gap="10px"
						onClick={(event) => event.stopPropagation()}
					>
						<ToggleButton.ToggleButton
							value={cybersourceCreateTripReports}
							onChange={setCybersourceCreateTripReports}
						/>
						<StyledP>{textCreateTripReports}</StyledP>
					</StyledRow>

					{cybersourceCreateTripReports && (
						<>
							<StyledRow alignItems="center" gap="10px" w="100%">
								<StyledP w="200px">
									{textCybersourceMerchantLegalName} :
								</StyledP>
								<TextBox.TextBox
									style={{ width: "calc(100% - 200px)" }}
									placeholder={
										placeholderCybersourceMerchantLegalName
									}
									value={
										typeof cybersourceMerchantLegalName ===
										"string"
											? cybersourceMerchantLegalName
											: ""
									}
									onChange={(next) => {
										if (typeof next === "string") {
											setCybersourceMerchantLegalName(
												next,
											);
										}
									}}
								/>
							</StyledRow>

							<StyledRow alignItems="center" gap="10px" w="100%">
								<StyledP w="200px">
									{textCybersourceMerchantWebsite} :
								</StyledP>
								<TextBox.TextBox
									style={{ width: "calc(100% - 200px)" }}
									placeholder={
										placeholderCybersourceMerchantWebsite
									}
									value={
										typeof cybersourceMerchantWebsite ===
										"string"
											? cybersourceMerchantWebsite
											: ""
									}
									onChange={(next) => {
										if (typeof next === "string") {
											setCybersourceMerchantWebsite(next);
										}
									}}
								/>
							</StyledRow>

							<StyledRow alignItems="center" gap="10px" w="100%">
								<StyledP w="200px">
									{textCybersourceSupportContacts} :
								</StyledP>
								<TextBox.TextBox
									style={{ width: "calc(100% - 200px)" }}
									placeholder={
										placeholderCybersourceSupportContacts
									}
									value={
										typeof cybersourceSupportContacts ===
										"string"
											? cybersourceSupportContacts
											: ""
									}
									onChange={(next) => {
										if (typeof next === "string") {
											setCybersourceSupportContacts(next);
										}
									}}
								/>
							</StyledRow>
						</>
					)}
				</>
			)}

			<StyledRow alignItems="center" gap="10px">
				<StyledP w="200px">{textCurrency}:</StyledP>
				<CybersourcePaymentsCurrency
					ref={setCurrencyRef}
					value={value}
					onChange={onChange}
				/>
			</StyledRow>
			<StyledRow alignItems="center" gap="10px">
				<StyledP w="200px">{textCountryIso}:</StyledP>
				<CybersourcePaymentsCountryIso
					ref={setCountryIsoRef}
					value={value}
					onChange={onChange}
				/>
			</StyledRow>
		</StyledColumn>
	);
}, InternalController);

const CybersourcePayment = memo(CybersourcePaymentBase);

declare namespace CybersourcePayment {
	type Ref = InternalController | null;
	type Controller = InternalController;
	interface PropsBase {
		value: Value;
		onChange: Dispatch<Value>;
		errors?: FieldErrors<any>;
	}
	type Props = PropsBase & RefAttributes<Ref>;

	interface Value extends PaymentSystem.Model {}
}

export default CybersourcePayment;
